import { X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { adminClient, type AdminOrganizationRow } from '@/api/adminClient';
import { OrganizationPicker, organizationName } from '@/components/org/OrganizationPicker';
import { useAdminAccess } from '@/hooks/useAdminAccess';

/** What publishing for testing sends: the chosen organizations, or nothing so the API keeps its own. */
export function testingOrganizationsInput(organizationIds: string[]): { testingOrganizationIds?: string[] } {
  return organizationIds.length > 0 ? { testingOrganizationIds: organizationIds } : {};
}

/**
 * The organizations that get a version published for testing, chosen before publishing. Studio admins pick
 * them from every Studio organization; anyone else sees who gets it.
 */
export function TestingOrganizationsField({ value, onChange, disabled }: { value: string[]; onChange: (organizationIds: string[]) => void; disabled?: boolean }) {
  const { isStudioAdmin } = useAdminAccess();
  const [organizations, setOrganizations] = useState<AdminOrganizationRow[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!isStudioAdmin) return undefined;
    let cancelled = false;
    adminClient
      .listOrganizations()
      .then((list) => {
        if (!cancelled) setOrganizations(list.items);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isStudioAdmin]);

  const nameOf = (organizationId: string) => organizationName(organizations, organizationId);

  return (
    <div className="space-y-2">
      <div className="text-xs font-medium text-foreground">Testing organizations</div>
      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5" aria-label="Testing organizations">
          {value.map((organizationId) => (
            <li
              key={organizationId}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/60 py-0.5 pl-2.5 pr-1 text-xs text-foreground"
            >
              {nameOf(organizationId)}
              {isStudioAdmin ? (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onChange(value.filter((entry) => entry !== organizationId))}
                  className="rounded-full p-0.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
                  aria-label={`Remove ${nameOf(organizationId)}`}
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">None chosen, so your own organization gets it to test.</p>
      )}
      {isStudioAdmin ? (
        loadFailed ? (
          <p className="text-xs text-muted-foreground">Could not load the Studio organizations to choose from.</p>
        ) : (
          <OrganizationPicker
            label="Add a testing organization"
            organizations={organizations}
            exclude={value}
            disabled={disabled}
            onPick={(organization) => onChange([...value, organization.organizationId])}
          />
        )
      ) : (
        <p className="text-xs text-muted-foreground">A Studio admin can change the testing organizations.</p>
      )}
    </div>
  );
}
